import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import {
  Button,
  TablePagination,
  Typography,
  TextField,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import Modal from "../Modal";
import Toast from "../../../utils/toast";
import createInstance from "../../../api/axiosinstance";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

// Search Bar Imports added
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";

const aiDishaApi = createInstance("DISHA");

const CssFormControl = styled(FormControl)({
  "& .MuiInputBase-root": {
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: 10 },
  },
});

const CssSelect = styled(Select)({
  "& .MuiSvgIcon-root": { textAlign: "center" },
});

const DistrictManagement = () => {
  const [states, setStates] = useState([]);
  const [activeDistrictList, setActiveDistrictList] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [districtName, setDistrictName] = useState("");
  const [districtCode, setDistrictCode] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deactivateId, setDeactiveId] = useState("");
  const [actionType, setActionType] = useState("");
  
  // FIX ISSUE 19: Added Search Term State
  const [searchTerm, setSearchTerm] = useState("");
  const { COLORS } = useThemeMode();

  // Mapping district names to standard codes
  const districtCodesMap = {
    "Bengaluru Urban": "BNU",
    "Mysuru": "MYS",
    "Mangaluru": "MLR",
    "Hubballi": "HBL",
    "Visakhapatnam": "VSKP",
    "Vijayawada": "VJA",
    "Tirupati": "TPT",
    "Guntur": "GNT",
    "Hyderabad": "HYD",
    "Warangal": "WGL",
    "Nizamabad": "NZB",
    "Khammam": "KHM",
    "Chennai": "MAA",
    "Coimbatore": "CBE",
    "Madurai": "IXM",
    "Tiruchirappalli": "TRZ",
    "Thiruvananthapuram": "TRV",
    "Kochi": "COK",
    "Kozhikode": "CCJ",
    "Thrissur": "TCR"
  };

  useEffect(() => {
    fetchActiveStates();
    fetchActiveDistricts();
  }, []);

  // Reset pagination when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const fetchActiveStates = async () => {
    try {
      const response = await aiDishaApi.get("/Get_active_states");
      setStates(response.data);
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const fetchActiveDistricts = async () => {
    try {
      const response = await aiDishaApi.get("/get-all-districts");
      setActiveDistrictList(response.data);
    } catch (error) {
      console.error("Error fetching districts", error);
    }
  };

  const fetchDistrictsByState = async (stateName) => {
    setSelectedState(stateName);
    setDistrictName("");
    setDistrictCode("");
    try {
      const response = await aiDishaApi.get(`/filter_districts?state_name=${encodeURIComponent(stateName)}`);
      if (response.data && response.data.state_districts) {
        setAvailableDistricts(response.data.state_districts);
      } else {
        setAvailableDistricts([]);
      }
    } catch (error) {
      console.error("Error fetching state districts:", error);
      setAvailableDistricts([]);
    }
  };

  const handleDistrictSelect = (selectedName) => {
    setDistrictName(selectedName);
    const code = districtCodesMap[selectedName] || selectedName.substring(0, 3).toUpperCase();
    setDistrictCode(code);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleModalClose = () => setIsModalOpen(false);
  const handleModalOpen = (districtId, action) => {
    setDeactiveId(districtId);
    setActionType(action);
    setIsModalOpen(true);
  };
  
  const handleDeactivateDistrict = async (districtId, currentStatus) => {
    try {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";
      const response = await aiDishaApi.patch(`/update_district_status?districtid=${districtId}&status=${newStatus}`);
      if (response.status === 200) {
        Toast.success(`District ${newStatus === "Active" ? "activated" : "deactivated"} successfully`);
        fetchActiveDistricts();
      }
    } catch (error) {
      console.error("Error toggling district status:", error);
      Toast.error("Failed to update district status");
    }
    setIsModalOpen(false);
  };

  const header = [
    { id: "District ID", name: "District Name", code: "District Code", state: "State Name", date: "Creation Date", action: "Action" },
  ];

  // FIX ISSUE 19: Filtering logic added for Search Bar trimming and case insensitivity
  const filteredRows = activeDistrictList.filter(
    (row) =>
      (row.State_name && row.State_name.toLowerCase().includes(searchTerm.trim().toLowerCase())) ||
      (row.district_name && row.district_name.toLowerCase().includes(searchTerm.trim().toLowerCase()))
  );

  const paginatedRows = filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  const handleClear = () => {
    setSelectedState("");
    setDistrictName("");
    setDistrictCode("");
    setAvailableDistricts([]);
    setSearchTerm("");
  };

  const handleDistrictSubmit = async (event) => {
    event.preventDefault();
    try {
      if (!selectedState || !selectedState.trim()) {
        Toast.error("State name cannot be empty"); return;
      }
      if (!districtName || !districtName.trim()) {
        Toast.error("District name cannot be empty"); return;
      }

      const response = await aiDishaApi.post(
        `/add-district?state_name=${selectedState}`,
        { district_name: districtName }
      );

      if (response.status === 200) {
        Toast.success("District saved successfully");
        handleClear();
        fetchActiveDistricts();
      }
    } catch (error) {
      Toast.error(error.response?.data?.detail || "Failed to save district");
    }
  };

  return (
    <div>
      <Box sx={{ marginTop: "30px", background: COLORS.primary, borderRadius: "10px", padding: "15px" }}>
        {/* FIX ISSUE 19: Header layout matching Skill Management with added Search Bar */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}>Add District</Typography>
            
          </Box>
          
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "350px", // Matched width of skill management form
              borderRadius: 2,
              background: COLORS.background, 
            }}
          >
            <IconButton
              type="button"
              sx={{ p: "8px", color: COLORS.text }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, 
                fontFamily: "Poppins", 
                color: COLORS.text,
               "& input::placeholder": {
                   color: `${COLORS.text} !important`,
                   opacity: 10, 
                 }, }} 
              placeholder="Search by District or State Name"
              inputProps={{ "aria-label": "search districts" }}
              value={searchTerm}
              onChange={handleSearch}
            />
          </Paper>
        </Box>

        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" sx={{ alignItems: "center", background: COLORS.primary, borderRadius: "10px" }}>
          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "10px" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>State Name <span style={{ color: "red" }}>*</span></Typography>
            <CssFormControl sx={{ width: "90%" }}>
              <InputLabel sx={{ display: "none" }}>Select State</InputLabel>
              <CssSelect
                size="small"
                value={selectedState}
                displayEmpty
                onChange={(e) => fetchDistrictsByState(e.target.value)}
                sx={{
                  width: "95%", color: COLORS.text, background: COLORS.background, borderRadius: 2, height: "35px",
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: COLORS.border, borderRadius: 2 } },
                  "& .MuiInputBase-input": { padding: "12px 14px" },
                }}
              >
                <MenuItem value="" disabled>Select State</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.state_id} value={state.state_name}>{state.state_name}</MenuItem>
                ))}
              </CssSelect>
            </CssFormControl>
          </Box>

          <Box gridColumn="span 3" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "10px" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>District Name <span style={{ color: "red" }}>*</span></Typography>
            <CssFormControl sx={{ width: "90%" }}>
              <InputLabel sx={{ display: "none" }}>Select District</InputLabel>
              <CssSelect
                size="small"
                displayEmpty
                value={districtName}
                onChange={(e) => handleDistrictSelect(e.target.value)}
                sx={{
                  width: "95%", color: COLORS.text, background: COLORS.background, borderRadius: 2, height: "35px",
                  "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: COLORS.border, borderRadius: 2 } },
                  "& .MuiSelect-select": { padding: "12px 14px !important", textAlign: "left" },
                }}
              >
                <MenuItem value="" disabled>District Name</MenuItem>
                {availableDistricts.map((dist, index) => (
                  <MenuItem key={index} value={dist} sx={{ color: "#033538" }}>
                    {dist}
                  </MenuItem>
                ))}
              </CssSelect>
            </CssFormControl>
          </Box>

          <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "left", marginBottom: "10px" }}>
            <Typography sx={{ fontFamily: "poppins", color: COLORS.text }}>District Code <span style={{ color: "red" }}>*</span></Typography>
            <TextField
              size="small"
              value={districtCode}
              onChange={(e) => setDistrictCode(e.target.value)}
              placeholder="District Code"
              disabled
              sx={{
                width: "90%",
                "& .MuiOutlinedInput-root": {
                  background: COLORS.background,
                  borderRadius: 2,
                  height: "35px",
                  color: COLORS.text,
                  "& fieldset": { borderColor: COLORS.border },
                },
              }}
            />
          </Box>
          <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "center", marginBottom: "10px" }}>
            <Button onClick={handleClear} sx={{ border: "1px solid", color: COLORS.button, "&:hover": { color: COLORS.hover_btn }, width: "120px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }}>
              Clear
            </Button>
          </Box>
          <Box gridColumn="span 2" sx={{ marginTop: "auto", textAlign: "center", marginBottom: "10px" }}>
            <Button onClick={handleDistrictSubmit} sx={{ border: "1px solid", background: COLORS.button, "&:hover": { background: COLORS.hover_btn }, color: COLORS.background, width: "120px", height: "40px", borderRadius: "10px", fontFamily: "poppins" }}>
              Save
            </Button>
          </Box>
        </Box>
      </Box>

      {/* TABLE SECTION */}
      <Box xs={1} sx={{ mt: 4 }}>
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={10}>
            <TableContainer component={Paper} sx={{ borderRadius: "10px", backgroundColor: "transparent", boxShadow: "none" }}>
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  <TableRow>
                    {header.map((n) => (
                      <React.Fragment key={n.id}>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.id}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.name}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.code}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.state}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.date}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: 0 }}>{n.action}</TableCell>
                      </React.Fragment>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* FIX ISSUE 19/16: Enhanced "No records found" UI logic */}
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                      <TableRow key={row.District_Id} sx={{ backgroundColor: COLORS.background }}>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.District_Id || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.district_name || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.disrtict_code || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>{row.State_name || "-"}</TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>
                          {row.creation_date ? new Date(row.creation_date).toLocaleDateString("en-GB") : "-"}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text, borderBottom: `1px solid ${COLORS.border}` }}>
                          <Button
                            variant="contained"
                            sx={{
                              backgroundColor: row.Status === "Active" ? COLORS.button_red_bg : COLORS.button_green_bg,
                              color: COLORS.text,
                              fontFamily: "poppins",
                              height: "24px",
                              padding: "2px 8px",
                              minWidth: "90px",
                              fontSize: "0.75rem",
                              "&:hover": { background: row.Status === "Active" ? COLORS.hover_red_btn : COLORS.hover_green_btn },
                            }}
                            onClick={() => handleModalOpen(row.District_Id, row.Status)}
                          >
                            {row.Status === "Active" ? "Deactivate" : "Activate"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow sx={{ background: COLORS.background }}>
                      <TableCell colSpan={6} sx={{ textAlign: "center", color: COLORS.text, padding: "20px" }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ background: COLORS.primary, color: COLORS.text }}
              />
            </TableContainer>
          </Grid>
        </Grid>
      </Box>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title="District"
        action={() => handleDeactivateDistrict(deactivateId, actionType)}
        actionType={actionType === "Active" ? "Deactivate" : "Activate"}
      />
    </div>
  );
};

export default DistrictManagement;